/**
 * Short-lived board VFX events (Phase C).
 * Emitted from Game at discrete combat commits — never scraped from combat log.
 * Headless sim can ignore (no listeners).
 */

import type { LayerId, Side } from "./board/types";
import {
  LAYER_ORDER,
  layerInward,
  layerOutward,
  slotsAdjacent,
} from "./board/types";
import type { BoardState } from "./board/board";
import type { Presence } from "./cards/types";

export type StrikeQuality = "meet" | "hit" | "weak" | "miss";

export type BoardFxEvent =
  | {
      type: "strike";
      layer: LayerId;
      slot: number;
      quality: StrikeQuality;
      steps: number;
    }
  | { type: "redeem"; layer: LayerId; slot: number }
  | {
      type: "silence";
      layer: LayerId;
      slot: number;
      side: Side;
    }
  | {
      type: "degrade";
      layer: LayerId;
      slot: number;
      side: Side;
      fromLevel: number;
      toLevel: number;
    }
  | { type: "still"; layer: LayerId; slot: number }
  | {
      /** Mutual level-step clash on a layer (both sides strained). */
      type: "strain";
      layer: LayerId;
      slot: number;
    }
  | {
      type: "boss.arrive";
      layer: LayerId;
      slot: number;
      name: string;
      claim: string;
    }
  | {
      type: "boss.settle";
      layer: LayerId;
      slot: number;
      name: string;
      claim: string;
    };

export function strikeQuality(
  steps: number,
  meet: boolean,
  correctTarget: boolean,
): StrikeQuality {
  if (steps <= 0) return "miss";
  if (meet) return "meet";
  if (correctTarget) return "hit";
  return "weak";
}

/** Continuous contest link for micro-FX (mirrors Game contest targeting). */
export interface ContestLink {
  fromLayer: LayerId;
  fromSlot: number;
  toLayer: LayerId;
  toSlot: number;
  /** 0..1 visual weight from contest rate */
  intensity: number;
  /** Who is applying pressure */
  fromSide: Side;
}

function effectiveContest(p: Presence): number {
  const c = p.verbs.contest ?? 0;
  if (c <= 0) return 0;
  // Soften residual presence slightly (same spirit as Game.wearRate)
  return c * (0.55 + p.level * 0.15);
}

/**
 * One link per contesting presence → preferred foe (same column, then strongest).
 * Used for continuous grind sparks between keeps and lies.
 */
export function collectContestLinks(board: BoardState): ContestLink[] {
  const out: ContestLink[] = [];

  for (const id of LAYER_ORDER) {
    const layer = board[id];
    for (let slot = 0; slot < layer.slots.length; slot++) {
      const p = layer.slots[slot];
      if (!p || p.level <= 0) continue;
      const contest = effectiveContest(p);
      if (contest <= 0) continue;

      let foeLayerId: LayerId = id;
      if (p.verbs.contestNextLayer) {
        const next = p.side === "player" ? layerOutward(id) : layerInward(id);
        if (!next) continue;
        foeLayerId = next;
      }
      const foeLayer = board[foeLayerId];

      const foes = foeLayer.slots
        .map((x, i) => ({ x, i }))
        .filter(
          (o) =>
            o.x &&
            o.x.side !== p.side &&
            o.x.level > 0 &&
            slotsAdjacent(slot, o.i),
        ) as {
        x: Presence;
        i: number;
      }[];
      if (!foes.length) continue;

      foes.sort((a, b) => {
        const colA = Math.abs(a.i - slot);
        const colB = Math.abs(b.i - slot);
        if (colA !== colB) return colA - colB;
        return b.x.level - a.x.level;
      });
      const foe = foes[0]!;
      out.push({
        fromLayer: id,
        fromSlot: slot,
        toLayer: foeLayerId,
        toSlot: foe.i,
        intensity: Math.min(1, contest / 4.5),
        fromSide: p.side,
      });
    }
  }

  return out;
}
